/**
 * One-time lift of browser-only vault folders onto the server.
 *
 * Folders and file→folder assignments used to live in localStorage, so they never left the
 * browser that made them. Now that they are real rows, anyone who already organised their vault
 * has that work sitting in local state which the server cannot see — so it has to be carried up
 * once, on their next visit, rather than silently abandoned.
 *
 * The ordering is deliberate and is the whole point of this module:
 *
 *   1. create every local folder on the server, building a local-id → server-id map;
 *   2. move each assigned item into its new server folder;
 *   3. ONLY THEN clear the local keys.
 *
 * If anything fails part-way the local keys are left untouched, so the next attempt can try
 * again from a known state. Clearing first — or clearing on partial success — would destroy the
 * only remaining copy of someone's organisation. A folder that was already lifted simply gets
 * created again as a duplicate on a retry, which is recoverable; losing the folders is not.
 */
import { createVaultFolder, moveVaultItemToFolder, type VaultFolder } from "../../morrowVault";
import {
  customVaultFoldersStorageKey,
  loadCustomVaultFolders,
  loadVaultItemAssignments,
  vaultItemAssignmentsStorageKey,
} from "./vaultCustomFolders";

export interface VaultFolderMigrationResult {
  /** Nothing local to lift — the overwhelmingly common case after the first run. */
  readonly skipped: boolean;
  readonly foldersCreated: number;
  readonly itemsMoved: number;
  /** True when every step succeeded and the local keys were cleared. */
  readonly cleared: boolean;
}

const NOTHING: VaultFolderMigrationResult = {
  skipped: true,
  foldersCreated: 0,
  itemsMoved: 0,
  cleared: false,
};

/**
 * Lift this user's localStorage folders to the server, if any remain.
 *
 * Returns what it did so the caller can refresh its folder list. Never throws: a failed lift
 * must not stop the vault page from rendering.
 */
export async function migrateLocalVaultFolders(
  userId: string | undefined,
): Promise<VaultFolderMigrationResult> {
  if (typeof window === "undefined") return NOTHING;

  const foldersKey = customVaultFoldersStorageKey(userId);
  const assignmentsKey = vaultItemAssignmentsStorageKey(userId);
  const localFolders = loadCustomVaultFolders(foldersKey);
  const assignments = loadVaultItemAssignments(assignmentsKey);

  if (localFolders.length === 0 && Object.keys(assignments).length === 0) return NOTHING;

  // 1. Recreate the folders, remembering which server folder each local id became.
  const idMap = new Map<string, string>();
  let foldersCreated = 0;
  let allOk = true;
  for (const local of localFolders) {
    const created: VaultFolder | null = await createVaultFolder(local.name);
    if (created) {
      idMap.set(local.id, created.id);
      foldersCreated += 1;
    } else {
      allOk = false;
    }
  }

  // 2. Re-file the items. An assignment pointing at a folder that failed to create is skipped
  //    rather than guessed at, and marks the lift incomplete so the local copy survives.
  let itemsMoved = 0;
  for (const [itemId, localFolderId] of Object.entries(assignments)) {
    const serverFolderId = idMap.get(localFolderId);
    if (!serverFolderId) {
      allOk = false;
      continue;
    }
    const moved = await moveVaultItemToFolder(itemId, serverFolderId);
    if (moved) itemsMoved += 1;
    else allOk = false;
  }

  // 3. Clear local state ONLY on a complete success.
  if (allOk) {
    try {
      window.localStorage.removeItem(foldersKey);
      window.localStorage.removeItem(assignmentsKey);
    } catch {
      // A storage write can fail (private mode, quota). The lift still worked; the next visit
      // would duplicate the folders, which is better than losing them.
      return { skipped: false, foldersCreated, itemsMoved, cleared: false };
    }
  }

  return { skipped: false, foldersCreated, itemsMoved, cleared: allOk };
}
